<script lang="ts">
  import { Button, Icon, InputDate, InputGroup } from '$shared/components';
  import type { CredentialingDatesFormModel } from '$shared/models/forms/CredentialingDatesFormModel';
  import { IconType } from '$shared/constants/IconType';

  interface Props {
    formData: CredentialingDatesFormModel;
    onchange?: (formData: CredentialingDatesFormModel) => void;
  }

  let { formData = $bindable(), onchange }: Props = $props();

  // Local state drives the {#each} loop — mutating a prop property directly
  // does not trigger Svelte 5 reactivity in the child's own template.
  let recredentialedDates: string[] = $state([]);

  // Sync from prop whenever the parent updates formData.recredentialedDates
  // (e.g. edit-form initialisation via setDates in the parent's onMount).
  $effect(() => {
    recredentialedDates = [...formData.recredentialedDates];
  });

  const dispatchChanges = (): void => {
    // Keep formData in sync so the parent's createPayload reads the correct values.
    formData.recredentialedDates = [...recredentialedDates];
    onchange?.(formData);
  };

  const onCredentialedDateChange = (value: string): void => {
    formData.setCredentialedDate(value?.trim());
    dispatchChanges();
  };

  const onNextCredentialingDateChange = (value: string): void => {
    formData.setNextCredentialingDate(value?.trim());
    dispatchChanges();
  };

  const onRecredentialedDateChange = (value: string, index: number): void => {
    recredentialedDates = recredentialedDates.map((d, i) => (i === index ? value?.trim() ?? '' : d));
    dispatchChanges();
  };

  const addRecredentialedDate = (): void => {
    recredentialedDates = [...recredentialedDates, ''];
    dispatchChanges();
  };

  const removeRecredentialedDate = (index: number): void => {
    if (index === -1) {
      return;
    }
    recredentialedDates = recredentialedDates.filter((_, i) => i !== index);
    dispatchChanges();
  };
</script>

<div class="cred-dates-form">
  <InputGroup>
    <!-- Credentialed Date -->
    <div class="input-wrap">
      <InputDate
        required={false}
        type="date"
        label="Initial Credentialed Date"
        name="credentialedDate"
        value={formData.credentialedDate}
        oninput={onCredentialedDateChange}
      />
    </div>
    <!-- Credentialed Date -->

    <!-- Next Credentialing Date -->
    <div class="input-wrap">
      <InputDate
        required={false}
        type="date"
        label="Next Credentialing Date"
        name="nextCredentialingDate"
        value={formData.nextCredentialingDate}
        oninput={onNextCredentialingDateChange}
      />
    </div>
  </InputGroup>
  <!-- Next Credentialing Date -->

  <InputGroup>
    <!-- Re-Credentialed Date -->
    <div class="input-wrap">
      <div class="input-label">Re-credentialed Dates</div>
      {#each recredentialedDates as recredentialedDate, index}
        <div class="multiple-fields">
          <InputDate
            name="recredentialedDate"
            required={false}
            value={recredentialedDate}
            type="date"
            oninput={(value) => onRecredentialedDateChange(value, index)}
          />
          <div class="icon">
            <Icon
              name="deleteReCredDate"
              value="icon-trash"
              type={IconType.Button}
              onclick={() => removeRecredentialedDate(index)}
            />
          </div>
        </div>
      {/each}
      <div class="add-button">
        <Button name="addReCredDate" icon="icon-plus" onclick={addRecredentialedDate}>Add</Button>
      </div>
    </div>
    <!-- Re-Credentialed Date -->
  </InputGroup>
</div>

<style>
  .input-wrap {
    margin-bottom: 24px;
  }
  .input-label {
    font-weight: 600;
    font-size: 12px;
    line-height: 20px;
    color: var(--black);
    margin-bottom: 8px;
  }
  .multiple-fields {
    display: flex;
    width: 100%;
    margin-bottom: 24px;
  }
  .multiple-fields .icon {
    display: flex;
    align-items: center;
    margin-left: 8px;
  }
  .add-button {
    margin-top: 8px;
  }
</style>
